import {
  GridFilterModel,
  GridCallbackDetails,
  GridSortModel,
  GridInitialState,
  GridRowGroupingModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium'
import isEqual from 'lodash/isEqual'
import { useEffect, useRef } from 'react'
import { track } from '@omnea-app/fe-analytics/useAnalytics'

const computeColumnVisibilityDiff = (model: GridColumnVisibilityModel, defaultModel: GridColumnVisibilityModel) => {
  const hiddenColumns = Object.keys(model).filter((field) => model[field] === false)
  const hiddenVsDefault = hiddenColumns.filter((field) => defaultModel[field] !== false)
  const shownVsDefault = Object.keys(defaultModel).filter((field) => defaultModel[field] === false && model[field] !== false)
  return { hiddenColumns, hiddenVsDefault, shownVsDefault }
}

export function useDataGridAnalytics({
  loading,
  rows,
  tableName,
  initialState,
  filterModel,
  sortModel,
  rowGroupingModel,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
}: {
  loading: boolean
  rows: unknown[]
  tableName?: string | null
  initialState?: GridInitialState
  filterModel?: GridFilterModel
  sortModel?: GridSortModel
  rowGroupingModel?: GridRowGroupingModel
  columnVisibilityModel?: GridColumnVisibilityModel
  defaultColumnVisibilityModel?: GridColumnVisibilityModel
}) {
  const loadStartRef = useRef<number | null>(null)
  const hasTrackedLoad = useRef(false)

  // Track table load time
  useEffect(() => {
    if (loading && loadStartRef.current === null) {
      loadStartRef.current = performance.now()
      hasTrackedLoad.current = false
    }
  }, [loading])

  useEffect(() => {
    // Fire on load completion even at zero rows (loadStartRef proves a real fetch ran, so this can't
    // fire on the pre-fetch mount): an empty table is still a view whose column visibility must be
    // captured, or the "share of views hiding column X" denominator silently drops empty loads.
    if (!loading && rows && loadStartRef.current && !hasTrackedLoad.current) {
      const durationMs = performance.now() - loadStartRef.current
      track('TableFirstRowsLoaded', {
        tableName: tableName || 'unknown',
        durationMs,
        rowCount: rows.length,
        filterModel,
        sortModel,
        initialState,
        rowGroupingModel,
        ...computeColumnVisibilityDiff(columnVisibilityModel ?? {}, defaultColumnVisibilityModel ?? {}),
      })
      hasTrackedLoad.current = true
      loadStartRef.current = null
    }
  }, [
    loading,
    rows,
    tableName,
    filterModel,
    initialState,
    sortModel,
    rowGroupingModel,
    columnVisibilityModel,
    defaultColumnVisibilityModel,
  ])

  const trackRowClick = () => {
    track('TableRowClicked', {
      tableName: tableName || 'unknown',
      filterModel,
      sortModel,
      initialState,
      rowGroupingModel,
    })
  }

  const lastTrackedFilterModelRef = useRef<GridFilterModel | null>(null)

  const trackFilterChange = (updatedFilterModel: GridFilterModel, _details: GridCallbackDetails) => {
    // MUI re-emits whenever its own state and the controlled prop diverge, each time as a fresh
    // object, so compare by value: an equal model is not a change the user made.
    if (isEqual(lastTrackedFilterModelRef.current, updatedFilterModel)) return
    lastTrackedFilterModelRef.current = updatedFilterModel

    track('TableFiltersChanged', {
      tableName: tableName || 'unknown',
      filterModel: updatedFilterModel,
      sortModel,
      initialState,
      rowGroupingModel,
    })
  }

  const trackSortChange = (updatedSortModel: GridSortModel, _details: GridCallbackDetails) => {
    track('TableSortModelChanged', {
      tableName: tableName || 'unknown',
      filterModel,
      sortModel: updatedSortModel,
      initialState,
      rowGroupingModel,
    })
  }

  const trackColumnVisibilityChange = (updatedModel: GridColumnVisibilityModel) => {
    track('TableColumnVisibilityChanged', {
      tableName: tableName || 'unknown',
      ...computeColumnVisibilityDiff(updatedModel, defaultColumnVisibilityModel ?? {}),
    })
  }

  return {
    trackRowClick,
    trackFilterChange,
    trackSortChange,
    trackColumnVisibilityChange,
  }
}
